import { formatRupiah } from '@safepos/shared';
import { Alert, Button, Card, PageHeader, SelectField, TextField } from '@safepos/ui';
import { useQuery } from '@tanstack/react-query';
import JsBarcode from 'jsbarcode';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../lib/api';
import type { ProductDto } from './CatalogPages';

interface Row {
  id: string;
  name: string;
  code: string;
  price: number;
}
const SIZES = {
  small: { label: 'Kecil 40×25 mm', w: '40mm', h: '25mm', bar: 22, font: 8 },
  medium: { label: 'Sedang 58×40 mm', w: '58mm', h: '40mm', bar: 38, font: 10 },
} as const;

/** Format barcode: EAN-13 bila 13 digit valid, selain itu CODE128 (SKU/barcode lain). */
function formatFor(code: string): 'EAN13' | 'CODE128' {
  return /^\d{13}$/.test(code) ? 'EAN13' : 'CODE128';
}

function Barcode({ value, height, font }: { value: string; height: number; font: number }) {
  const ref = useRef<SVGSVGElement>(null);
  useEffect(() => {
    if (!ref.current) return;
    try {
      JsBarcode(ref.current, value, {
        format: formatFor(value),
        width: 1.4,
        height,
        fontSize: font,
        margin: 0,
        displayValue: true,
      });
    } catch {
      try {
        JsBarcode(ref.current, value, {
          format: 'CODE128',
          width: 1.4,
          height,
          fontSize: font,
          margin: 0,
        });
      } catch {
        /* nilai tak bisa dikodekan */
      }
    }
  }, [value, height, font]);
  return <svg ref={ref} style={{ maxWidth: '100%' }} />;
}

/** Cetak label barcode (rak/produk) lewat dialog cetak browser. */
export function LabelsPage() {
  const { data } = useQuery({
    queryKey: ['products', 'labels'],
    queryFn: () => api.get<{ data: ProductDto[] }>('/products?limit=200&active=true'),
  });
  const [q, setQ] = useState('');
  const [size, setSize] = useState<keyof typeof SIZES>('small');
  const [copies, setCopies] = useState<Record<string, number>>({});
  const rows: Row[] = useMemo(
    () =>
      (data?.data ?? []).flatMap((p) =>
        p.variants
          .filter((v) => v.barcode || v.sku)
          .map((v) => ({
            id: v.id,
            name: p.type === 'variant' ? `${p.name} ${v.name}` : p.name,
            code: (v.barcode || v.sku)!,
            price: v.price,
          })),
      ),
    [data],
  );
  const shown = rows.filter(
    (r) => !q.trim() || r.name.toLowerCase().includes(q.toLowerCase()) || r.code.includes(q),
  );
  const picked = rows.filter((r) => (copies[r.id] ?? 0) > 0);
  const total = picked.reduce((s, r) => s + (copies[r.id] ?? 0), 0);
  const sz = SIZES[size];
  return (
    <>
      <style>{`
        #labels-print { display: none; }
        @media print {
          body * { visibility: hidden !important; }
          #labels-print, #labels-print * { visibility: visible !important; }
          #labels-print { display: flex; flex-wrap: wrap; position: absolute; left: 0; top: 0; }
        }
      `}</style>
      <PageHeader
        title="Label Barcode"
        subtitle="Pilih produk dan jumlah label, lalu cetak. EAN-13 bila 13 digit, selain itu CODE 128."
      />
      <Card>
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <div className="w-64">
            <TextField
              label="Cari"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="nama / SKU / barcode"
            />
          </div>
          <div className="w-48">
            <SelectField
              label="Ukuran label"
              value={size}
              onChange={(e) => setSize(e.target.value as keyof typeof SIZES)}
            >
              {Object.entries(SIZES).map(([k, v]) => (
                <option key={k} value={k}>
                  {v.label}
                </option>
              ))}
            </SelectField>
          </div>
          <Button disabled={total === 0} onClick={() => window.print()}>
            Cetak {total} label
          </Button>
          {total > 0 && (
            <Button variant="ghost" onClick={() => setCopies({})}>
              Kosongkan
            </Button>
          )}
        </div>
        {rows.length === 0 && <Alert tone="info">Belum ada produk ber-SKU/barcode.</Alert>}
        <ul className="max-h-[55vh] divide-y overflow-auto">
          {shown.map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm">
              <span>
                <b>{r.name}</b>
                <span className="block font-mono text-xs text-slate-500">
                  {r.code} · {formatRupiah(r.price)}
                </span>
              </span>
              <input
                aria-label={`Jumlah label ${r.name}`}
                className="w-20 rounded border px-2 py-1 text-right"
                inputMode="numeric"
                placeholder="0"
                value={copies[r.id] ?? ''}
                onChange={(e) =>
                  setCopies({
                    ...copies,
                    [r.id]: Math.min(200, Number(e.target.value.replace(/\D/g, '')) || 0),
                  })
                }
              />
            </li>
          ))}
        </ul>
      </Card>
      <div id="labels-print">
        {picked.flatMap((r) =>
          Array.from({ length: copies[r.id] ?? 0 }, (_, i) => (
            <div
              key={`${r.id}-${i}`}
              style={{
                width: sz.w,
                height: sz.h,
                padding: '1.5mm',
                boxSizing: 'border-box',
                pageBreakInside: 'avoid',
                border: '0.1mm dashed #ccc',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  fontSize: sz.font,
                  fontWeight: 700,
                  lineHeight: 1.1,
                  maxHeight: '2.4em',
                  overflow: 'hidden',
                }}
              >
                {r.name}
              </div>
              <Barcode value={r.code} height={sz.bar} font={sz.font} />
              <div style={{ fontSize: sz.font + 1, fontWeight: 700 }}>{formatRupiah(r.price)}</div>
            </div>
          )),
        )}
      </div>
    </>
  );
}
